import { keyBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { FieldOptionBase } from 'api/tasks';
import { Filter } from 'components/molecules/filters/types';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import {
  FieldType,
  FilterableField,
  SingleSelectField,
  SourceType,
} from 'components/organisms/tasks/fields';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { useFilters } from 'components/organisms/tasks/use-filters';
import {
  TemplateItemInfo,
  useTemplateInsightFilter,
} from 'components/organisms/template-insight/template-filter/use-filter';
import { useTranslation } from 'hooks/use-translation';
import { useProjectTemplates } from 'queries';
import { getRandomColor } from 'utils/get-random-color';

interface UseTemplateFilterProps {
  fields: FilterableField[];
  isFieldsLoading: boolean;
}

export const useTemplateField = (
  fieldId: number,
): {
  templateField: SingleSelectField;
  isLoading: boolean;
} => {
  const { data: templates, isLoading } = useProjectTemplates();
  const fieldLabel = useTranslation('project.fields.template');

  return useMemo(
    () => ({
      templateField: {
        sourceType: SourceType.Project,
        type: FieldType.SingleSelect,
        customField: false,
        fieldId,
        fieldName: 'template',
        fieldLabel,
        metaData: {
          options: (templates ?? []).map((x) => ({
            label: x.templateName ?? '',
            value: x.templateId as number,
            color: getRandomColor(x.templateId as number),
            active: true,
          })),
        },
        private: true,
      },
      isLoading,
    }),
    [fieldId, fieldLabel, isLoading, templates],
  );
};

export const useTemplateFilters = ({
  fields,
  isFieldsLoading,
}: UseTemplateFilterProps): {
  filter: Filter;
  selectFilter: (next: Filter) => void;
  clearFilters: () => void;
  replaceFilter: (next: Filter) => void;
  updateTemplateInFilter: (value: number, shouldReplace?: boolean) => Filter;
  templateSelection: {
    id?: number;
    data?: FieldOptionBase<number>;
  };
  savedId?: number;
  templateOptions: { ids: number[]; items: Array<TemplateItemInfo> };
  fields_lookup_by_name: Record<string, FilterableField>;
  isTemplateFieldLoading: boolean;
  templateField: SingleSelectField;
} => {
  const { templateField, isLoading: isTemplateFieldLoading } =
    useTemplateField(-1);

  const fields_lookup_by_name = useMemo(
    (): Record<string, FilterableField> =>
      keyBy(isFieldsLoading ? [] : [...fields, templateField], 'fieldName'),
    [isFieldsLoading, fields, templateField],
  );

  const {
    filter,
    clearFilters: _clearFilters,
    selectFilter,
    replaceFilter,
  } = useFilters(fields_lookup_by_name, isFieldsLoading ? 'loading' : 'loaded');

  const clearFilters = useCallback(() => {
    const templateFilter = filter.criteria.find(
      (x) => x.field.fieldName === 'template',
    );

    _clearFilters();
    if (templateFilter) {
      selectFilter({ type: QueryCombinerType.All, criteria: [templateFilter] });
    }
  }, [_clearFilters, filter.criteria, selectFilter]);

  const template_lookup_by_name = useMemo(
    () => keyBy(isTemplateFieldLoading ? [] : [templateField], 'fieldName'),
    [isTemplateFieldLoading, templateField],
  );

  const {
    updateTemplateInFilter,
    selection: templateSelection,
    savedId,
    options: templateOptions,
  } = useTemplateInsightFilter({
    filter,
    templateField,
    selectFilter,
    replaceFilter,
    template_lookup_by_name,
  });

  return {
    filter,
    selectFilter,
    clearFilters,
    replaceFilter,
    updateTemplateInFilter,
    templateSelection,
    savedId,
    templateOptions,
    fields_lookup_by_name,
    isTemplateFieldLoading,
    templateField,
  };
};

export const removeTemplateFromFilter = (
  options: FieldFilter[],
): FieldFilter[] => options.filter((x) => x.field.fieldName !== 'template');
